// filepath: c:\Users\m_her\OneDrive\Documentos\GitHub\OMS-AMERISA\Frontend\src\pages\Client-pool.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import logoamerisa from '../assets/logoamerisa.png';
import api from '../api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Badge } from '../components/ui/badge';
import * as XLSX from 'xlsx';
import { Download, Calendar, Truck, FileSearch, Building, FileCheck2, Camera, FileText, CheckCircle, XCircle, Menu } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Constantes para estados de orden
const ORDER_STATUS = {
  PENDING: 'PENDIENTE',
  ARRIVAL: 'ARRIBADO',
  IN_TRANSIT: 'TRANSITO',
  DELIVERED: 'ENTREGADO',
  CANCELLED: 'CANCELADO',
  ON_CLIENT: 'EN CLIENTE',
  CREATED: 'CREADO',
  CLOSED: 'FINALIZADO',
};

// Estados válidos para validación de órdenes
const VALIDATION_ELIGIBLE_STATUSES = [ORDER_STATUS.DELIVERED, ORDER_STATUS.CLOSED];

// Función de utilidad para normalizar fechas con formatos problemáticos
const normalizeDateString = (dateStr) => {
  if (!dateStr) return null;
  
  // Crear una copia de la cadena para no modificar el original
  let normalizedDate = String(dateStr);
  
  // Quitar el "+" al final si existe
  normalizedDate = normalizedDate.replace(/\+$/, '');
  
  // Formatear los milisegundos correctamente (limitarlos a 3 dígitos)
  normalizedDate = normalizedDate.replace(/\.(\d{3})\d+/, '.$1');
  
  return normalizedDate;
};

const OrderGrid = () => {
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);  // Abierto por defecto solo en desktop
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);  // Estado para detectar dispositivos móviles
  
  // Efecto para manejar el cambio de tamaño de la ventana
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Si cambia de móvil a desktop, abrir el sidebar
      if (!mobile && !sidebarOpen) {
        setSidebarOpen(true);
      }
      // Si cambia de desktop a móvil, cerrar el sidebar
      if (mobile && sidebarOpen) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen]);
  
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [validatedOrders, setValidatedOrders] = useState({}); // Estado para órdenes validadas
  const [activeItem, setActiveItem] = useState('Flowboard');  
  const [filters, setFilters] = useState({
    orderNumber: '',
    truck: '',
    appointmentRange: [null, null],
    client: '',
    status: '',
    evidenceType: '', // '' = All, 'photo' = Solo con fotos, 'pdf' = Solo con PDF, 'both' = Con ambos, 'none' = Sin evidencias
  });  const [isLoading, setIsLoading] = useState(true);  const [userRole, setUserRole] = useState('');  const [assignedClients, setAssignedClients] = useState([]);

  const navigate = useNavigate();

  // Obtener el rol del usuario
  useEffect(() => {
    const role = localStorage.getItem('role');
    setUserRole(role || '');

    const fetchUserInfo = async () => {
      try {
        // Si el usuario tiene rol client, obtenemos sus clientes asignados para mostrarlos en la UI
        if (role === 'client') {
          const userInfoResponse = await api.get('/api/v1/auth/me');
          const userId = userInfoResponse.data.id;
          if (userId) {
            const clientsResponse = await api.get(`/api/v1/auth/users/${userId}/clients`);
            setAssignedClients(clientsResponse.data || []);
          }
        }
      } catch (error) {
        console.error('Error obteniendo información del usuario:', error);
      }
    };

    fetchUserInfo();
  }, []);

  // Usar el nuevo endpoint client-pool que filtra órdenes en el backend
  const fetchOrders = async () => {
    try {
      setIsLoading(true);
      // Usamos el endpoint que filtra las órdenes directamente en el backend
      const response = await api.get('/api/v1/orders/client-pool');
      //const response = await api.get('/api/v1/orders/client-pool?debug=true');// usar para trabajar con esta madre sin cargar todas las ordenes mas que las de pruebas 

      // Sort orders by appointment date (most recent first)
      const sortedOrders = response.data.sort((a, b) => {
        if (!a.Appointment) return 1;
        if (!b.Appointment) return -1;
        
        return new Date(b.Appointment) - new Date(a.Appointment);
      });
      
      setOrders(sortedOrders);
      setFilteredOrders(sortedOrders);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setIsLoading(false);
    }
  };

  // Ejecutar fetchOrders una vez que tengamos la información del rol
  useEffect(() => {
    if (userRole) {
      fetchOrders();
    }
  }, [userRole]);

  // Function to check if a date is within a range
  const isDateInRange = (dateString, startDate, endDate) => {
    if (!dateString) return false;
    
    const dateParts = dateString.split('T')[0].split('-');
    if (dateParts.length !== 3) return false;
    
    const date = new Date(
      parseInt(dateParts[0]), 
      parseInt(dateParts[1]) - 1,
      parseInt(dateParts[2])
    );
    date.setHours(0, 0, 0, 0);
    
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    
    return date >= start && date <= end;
  };
  
  useEffect(() => {
    const applyFilters = () => {
      const filtered = orders.filter((order) => {
        const matchesOrderNumber = order.Order_number?.toLowerCase().includes(filters.orderNumber?.toLowerCase() || '') ?? false;
        const matchesTruck = filters.truck
          ? (order.Truck || '').toLowerCase().includes(filters.truck.toLowerCase())
          : true;
        const matchesClient = order.customer?.toLowerCase().includes(filters.client?.toLowerCase() || '') ?? false;
        
        let matchesAppointment = true;
        if (filters.appointmentRange[0] && filters.appointmentRange[1]) {
          matchesAppointment = isDateInRange(
            order.Appointment,
            filters.appointmentRange[0],
            filters.appointmentRange[1]
          );
        }
        
        const matchesStatus = filters.status 
          ? order.Order_status?.toLowerCase() === filters.status.toLowerCase() 
          : true;
        
        // Nuevo: filtro de subestatus
        const matchesSubstatus = filters.status && filters.substatus
          ? (order.order_Substatus || '').toLowerCase() === filters.substatus.toLowerCase()
          : true;

        // Filtro de evidencias
        let matchesEvidence = true;
        if (filters.evidenceType) {
          const hasPhoto = hasPhotoEvidence(order);
          const hasPDF = hasPdfEvidence(order);
          
          switch (filters.evidenceType) {
            case 'photo':
              matchesEvidence = hasPhoto;
              break;
            case 'pdf':
              matchesEvidence = hasPDF;
              break;
            case 'both':
              matchesEvidence = hasPhoto && hasPDF;
              break;
            case 'none':
              matchesEvidence = !hasPhoto && !hasPDF;
              break;
            default:
              matchesEvidence = true;
          }
        }

        return matchesOrderNumber && matchesTruck && matchesClient && matchesAppointment && matchesStatus && matchesSubstatus && matchesEvidence;
      });
      
      const sortedFiltered = filtered.sort((a, b) => {
        if (!a.Appointment) return 1;
        if (!b.Appointment) return -1;
        return new Date(b.Appointment) - new Date(a.Appointment);
      });
      
      setFilteredOrders(sortedFiltered);
    };

    applyFilters();  }, [filters, orders]);
  
  // Función para validar automáticamente órdenes con más de 30 días
  const autoValidateOldOrders = async (ordersToCheck) => {
    const currentDate = new Date();
    const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000; // 30 días en milisegundos
    let autoValidationsPerformed = 0;
    
    // Filtrar todas las órdenes que cumplen con los criterios para validación automática
    const ordersForAutoValidation = ordersToCheck.filter(order => {
      const isEligibleStatus = VALIDATION_ELIGIBLE_STATUSES.includes(order.Order_status);
      
      // Manejar metrics como array y acceder al primer elemento si existe
      const metricsItem = Array.isArray(order.metrics) && order.metrics.length > 0 
                           ? order.metrics[0] 
                           : null;
                           
      const hasPodtime = metricsItem && metricsItem.order_Podtime;
      const isNotValidatedByUser = !order.signoff_user;
      const isNotValidatedByDate = !order.signoff_date;
        
      let podTimeValid = false;
      let exceeds30Days = false;
        
      if (hasPodtime) {
        // Usar la función de normalización para estandarizar el formato de fecha
        const normalizedDateStr = normalizeDateString(metricsItem.order_Podtime);
        const podTime = new Date(normalizedDateStr);
        podTimeValid = podTime instanceof Date && !isNaN(podTime.getTime());
        exceeds30Days = podTimeValid && (currentDate - podTime) > thirtyDaysInMs;
      }
      
      return isEligibleStatus && hasPodtime && isNotValidatedByUser && isNotValidatedByDate && podTimeValid && exceeds30Days;
    });
    
    if (ordersForAutoValidation.length === 0) {
      return; // No hay órdenes para validación automática
    }
    
    // Procesar las validaciones en grupos para no sobrecargar el servidor
    const BATCH_SIZE = 5; // Tamaño del lote para procesar
    const newValidatedOrders = {};
    
    // Procesar en lotes
    for (let i = 0; i < ordersForAutoValidation.length; i += BATCH_SIZE) {
      const batch = ordersForAutoValidation.slice(i, i + BATCH_SIZE);
      
      // Crear promesas para el lote actual
      const batchPromises = batch.map(async (order) => {
        try {
          // Preparar datos para la validación
          const validationData = { 
            order_number: order.Order_number,
            auto_validate: true,
            system_validation: true
          };
          
          const response = await api.post('/api/v1/orders/validate', validationData);
          
          autoValidationsPerformed++;
          
          // Guardar el resultado para actualizar el estado
          newValidatedOrders[order.Order_number] = {
            validated: true,
            timestamp: response.data.timestamp || new Date().toISOString(),
            user: "sistema"
          };
          
          return true;
        } catch (error) {
          return false;
        }
      });
      
      // Esperar a que se completen todas las promesas del lote actual
      await Promise.all(batchPromises);
    }
      
    // Actualizar el estado local con todas las validaciones automáticas
    if (Object.keys(newValidatedOrders).length > 0) {
      setValidatedOrders(prev => ({
        ...prev,
        ...newValidatedOrders
      }));
      
      toast.info(`Se validaron automáticamente ${autoValidationsPerformed} órdenes que excedieron 30 días sin validación.`, {
        position: "top-right",
        autoClose: 10000
      });
    }
  };

  // Efecto para inicializar el estado de validación cuando se cargan las órdenes
  useEffect(() => {
    // Inicializamos el estado de validación basado en los datos que ya vienen en las órdenes
    const validationState = {};
    
    orders.forEach(order => {
      if (order.signoff_user && order.signoff_date) {
        validationState[order.Order_number] = {
          validated: true,
          timestamp: order.signoff_date,
          user: order.signoff_user
        };
      }
    });
    
    // Actualizamos el estado con todas las órdenes validadas
    if (Object.keys(validationState).length > 0) {
      setValidatedOrders(prev => ({
        ...prev,
        ...validationState
      }));
    }
    
    // Ejecutar la validación automática para órdenes con más de 30 días
    autoValidateOldOrders(orders);
  }, [orders]);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates;
    setFilters((prev) => ({
      ...prev,
      appointmentRange: [start, end],
    }));
  };

  const getClientATA = (order) => {
    return order.metrics?.length > 0 ? order.metrics[0].order_ClientATA || "N/A" : "N/A";
  };

  // Función para verificar si un embarque tiene evidencia fotográfica
  const hasPhotoEvidence = (order) => {
    return Boolean(order.photos_drive_ids || order.imagen_evidencia || order.photo_evidence);
  };
  
  // Función para verificar si un embarque tiene evidencia PDF
  const hasPdfEvidence = (order) => {
    return Boolean(order.evidence_drive_id || order.pdf_carta_porte || order.pdf_evidence);
  };
  // Función para manejar la validación de órdenes
  const handleValidateOrder = async (orderNumber, e) => {
    e.stopPropagation(); // Evitar que se propague al onClick de la fila
    
    try {
      // Verificar si la orden es elegible para validación
      const currentOrder = orders.find(order => order.Order_number === orderNumber);
      
      if (!currentOrder || !VALIDATION_ELIGIBLE_STATUSES.includes(currentOrder.Order_status)) {
        toast.error('Esta orden no puede ser validada porque no está en estado ENTREGADO o FINALIZADO', {
          position: "top-right",
          autoClose: 5000
        });
        return;
      }
      
      // Mostrar indicador de carga
      const toastId = toast.loading("Validando orden...", {
        position: "top-right"
      });
        // Llamar al endpoint para validar la orden
      const response = await api.post('/api/v1/orders/validate', { order_number: orderNumber });
      
      // Actualizar el estado local para reflejar la validación
      setValidatedOrders(prev => ({
        ...prev,
        [orderNumber]: {
          validated: true,
          timestamp: response.data.timestamp || new Date().toISOString(),
          user: response.data.user || 'Usuario actual'
        }
      }));
        // Actualizar el toast de carga a éxito
      toast.update(toastId, { 
        render: `La orden ${orderNumber} ha sido validada exitosamente.`, 
        type: "success", 
        isLoading: false,
        autoClose: 5000
      });
      
    } catch (error) {
      console.error('Error al validar orden:', error);
      
      // Mostrar mensaje de error apropiado
      const errorMessage = error.response?.data?.detail || 'Error al validar la orden. Por favor, inténtelo de nuevo.';
      
      // Mostrar un mensaje más amigable para el usuario con Toast
      toast.error(`No se pudo validar la orden: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000
      });
    }
  };

  // Función para verificar si una orden ha sido validada
  const isOrderValidated = (orderNumber) => {
    // Primero verificamos el estado local por rendimiento
    if (validatedOrders[orderNumber]?.validated) {
      return true;
    }
    
    // Si no está en el estado local, la orden no está validada
    return false;
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };
  const formatDate = (dateString) => {
    if (!dateString) return 'PENDIENTE';
    
    // Usar la función de normalización para limpiar la fecha
    const normalizedDate = normalizeDateString(dateString);
    const date = new Date(normalizedDate);
    if (isNaN(date.getTime())) return 'PENDIENTE';

    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });
  };

  const formatAppointmentDateTime = (appointmentDate, appointmentTime) => {
    if (!appointmentDate) return 'N/A';
    
    if (!appointmentTime) return formatDate(appointmentDate);
    
    const dateParts = appointmentDate.split('T')[0].split('-');
    if (dateParts.length !== 3) return 'Formato inválido';
    
    const year = parseInt(dateParts[0]);
    const month = parseInt(dateParts[1]) - 1;
    const day = parseInt(dateParts[2]);
    
    const timeParts = appointmentTime.split(':');
    const hour = parseInt(timeParts[0]) || 0;
    const minute = parseInt(timeParts[1]) || 0;
    const second = parseInt(timeParts[2]) || 0;
    
    const appointmentDateTime = new Date(year, month, day, hour, minute, second);
    
    return formatDate(appointmentDateTime);
  };

  const getStatusBadge = (status) => {
    const normalizedStatus = status?.toUpperCase().replace(' ', '_');

    const styles = {
      PENDIENTE: 'bg-yellow-100 text-yellow-800',
      EN_CLIENTE: 'bg-indigo-500 text-white whitespace-nowrap',
      ENTREGADO: 'bg-green-100 text-green-800',
      CANCELADO: 'bg-red-100 text-red-800',
      ARRIBADO: 'bg-sky-600 text-white',
      TRANSITO: 'bg-green-500 text-white',
      FINALIZADO: 'bg-green-700 text-white',
      CREADO: 'bg-gray-100 text-gray-800',
      ASIGNADO: 'bg-blue-200 text-blue-800',
    };

    const statusClass = styles[normalizedStatus] || 'bg-gray-100 text-gray-800';

    return (
      <Badge className={`px-2 py-1 rounded-full text-sm whitespace-nowrap ${statusClass}`}>
        {status || 'N/A'}
      </Badge>
    );
  };

  const orderStatuses = [...new Set(orders.map(order => order.Order_status).filter(Boolean))];

  const handleOrderClick = (orderNumber) => {
    navigate(`/Clientview?do=${orderNumber}`);
  };

  // Función para mostrar el nombre del cliente o clientes asignados
  const getClientDisplayText = () => {
    if (assignedClients.length === 0) return "";
    if (assignedClients.length === 1) return assignedClients[0].nombre_cuenta;
    return `${assignedClients.length} clientes asignados`;
  };

  const handleExportToExcel = () => {
    // Preparar los datos para exportar
    const dataToExport = filteredOrders.map(order => ({
      'DO': order.Order_number,
      'Camión': order.Truck || 'N/A',
      'Cliente': order.customer,
      'Destino': order.City,
      'Cita': formatAppointmentDateTime(order.Appointment, order.Appointment_hr),
      'Arribo': formatDate(getClientATA(order)),
      'Estatus': order.Order_status,
      'SubEstatus': order.order_Substatus || '-',
      'Evidencia Fotográfica': hasPhotoEvidence(order) ? 'Sí' : 'No',
      'Evidencia PDF': hasPdfEvidence(order) ? 'Sí' : 'No',
      'Validada': isOrderValidated(order.Order_number) ? 'Sí' : 'No'
    }));

    // Crear el libro de trabajo y la hoja
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Órdenes');

    // Generar el archivo y descargarlo
    const fileName = `ordenes_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  return (
    <div className="flex h-screen bg-gray-50 relative">
      {/* Overlay para móviles cuando el sidebar está abierto */}
      {sidebarOpen && isMobile && (
        <div 
          className="fixed inset-0 bg-gray-900 bg-opacity-50 z-20 md:hidden sidebar-overlay" 
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      
      {/* Sidebar - fixed position con z-index alto */}
      <div 
        className="fixed top-0 left-0 h-full z-30 transition-all duration-300 ease-in-out bg-gray-800"
        style={{
          width: sidebarOpen ? '16rem' : '4rem',
          transform: (!sidebarOpen && isMobile) ? 'translateX(-100%)' : 'translateX(0)'
        }}
      >
        <Sidebar
          isOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          activeItem={activeItem}
          setActiveItem={setActiveItem}
          onLogout={handleLogout}
        />
      </div>
      
      {/* Main content - adjust for sidebar width */}
      <div 
        className={`flex-1 transition-all duration-300 overflow-y-auto`}
        style={{
          marginLeft: isMobile ? '0' : (sidebarOpen ? '16rem' : '4rem')
        }}
      >
        {/* Header with brand */}
        <div className="bg-gradient-to-r from-sky-800 to-blue-600 text-white p-4 md:p-6 shadow-lg sticky top-0 z-10">
          <div className="container mx-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {/* Botón para abrir/cerrar el sidebar en móviles */}
                <button 
                  onClick={() => setSidebarOpen(!sidebarOpen)} 
                  className="md:hidden p-1 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-white"
                >
                  <Menu size={24} />
                </button>
                <img src={logoamerisa} alt="Logo" className="h-8 md:h-10" />
                <h1 className="text-xl md:text-2xl font-bold">Seguimiento de Ordenes</h1>
              </div>
              {userRole === 'client' && assignedClients.length > 0 && (
                <div className="text-sm">
                  <span className="opacity-80">Cliente(s):</span>{" "}
                  <span className="font-medium">{getClientDisplayText()}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main content area */}
        <div className="w-full max-w-7xl mx-auto px-3 md:px-4 py-4 md:py-8">
          {/* Filter Bar - Responsive Card Style */}
          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Filtros de Búsqueda</h2>            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
              <div className="space-y-1">
                <label htmlFor="orderNumber" className="text-sm text-gray-600 font-medium">Número de Orden</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FileSearch size={18} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="orderNumber"
                    name="orderNumber"
                    value={filters.orderNumber}
                    onChange={handleFilterChange}
                    placeholder="Buscar por DO"
                    className="pl-10 p-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div className="space-y-1">
                <label htmlFor="truck" className="text-sm text-gray-600 font-medium">Camión</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Truck size={18} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="truck"
                    name="truck"
                    value={filters.truck}
                    onChange={handleFilterChange}
                    placeholder="Buscar por camión"
                    className="pl-10 p-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div className="space-y-1">
                <label htmlFor="appointmentRange" className="text-sm text-gray-600 font-medium">Rango de Fechas</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar size={18} className="text-gray-400" />
                  </div>
                  <DatePicker
                    id="appointmentRange"
                    selected={filters.appointmentRange[0]}
                    onChange={handleDateRangeChange}
                    startDate={filters.appointmentRange[0]}
                    endDate={filters.appointmentRange[1]}
                    selectsRange
                    placeholderText="Seleccionar fechas"
                    className="pl-10 p-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div className="space-y-1">
                <label htmlFor="client" className="text-sm text-gray-600 font-medium">Cliente</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Building size={18} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="client"
                    name="client"
                    value={filters.client}
                    onChange={handleFilterChange}
                    placeholder="Buscar por cliente"
                    className="pl-10 p-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div className="space-y-1">
                <label htmlFor="status" className="text-sm text-gray-600 font-medium">Estatus</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FileCheck2 size={18} className="text-gray-400" />
                  </div>
                  <select
                    id="status"
                    name="status"
                    value={filters.status}
                    onChange={handleFilterChange}
                    className="pl-10 p-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Todos los estatus</option>
                    {orderStatuses.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              </div>
              {/* Filtro de SubEstatus, solo visible si hay un estatus seleccionado */}
              {filters.status && (
                <div className="space-y-1">
                  <label htmlFor="substatus" className="text-sm text-gray-600 font-medium">SubEstatus</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FileCheck2 size={18} className="text-gray-400" />
                    </div>
                    <select
                      id="substatus"
                      name="substatus"
                      value={filters.substatus || ''}
                      onChange={handleFilterChange}
                      className="pl-10 p-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Todos los subestatus</option>
                      {/* Subestatus dinámicos según el estatus seleccionado */}
                      {Array.from(new Set(orders.filter(o => o.Order_status === filters.status && o.order_Substatus).map(o => o.order_Substatus))).map(sub => (
                        <option key={sub} value={sub}>{sub}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              
              <div className="space-y-1">
                <label htmlFor="evidenceType" className="text-sm text-gray-600 font-medium">Evidencias</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Camera size={18} className="text-gray-400" />
                  </div>
                  <select
                    id="evidenceType"
                    name="evidenceType"
                    value={filters.evidenceType}
                    onChange={handleFilterChange}
                    className="pl-10 p-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Todas las órdenes</option>
                    <option value="photo">Con fotos</option>
                    <option value="pdf">Con PDF</option>
                    <option value="both">Con ambas evidencias</option>
                    <option value="none">Sin evidencias</option>
                  </select>
                </div>
              </div>
            </div>
          </div>          
          
          {/* Results Count and Summary */}          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-semibold text-gray-800">
                Resultados: <span className="text-blue-600">{filteredOrders.length}</span>
              </h2>
              {filteredOrders.length > 0 && (
                <button
                  onClick={handleExportToExcel}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                  title="Exportar órdenes filtradas a Excel"
                >
                  <Download size={16} />
                  <span>Exportar</span>
                </button>
              )}
            </div>
            <div className="text-sm text-gray-500">
              Última actualización: {new Date().toLocaleTimeString()}
            </div>
          </div>
          
          {/* Aviso de validación de 30 días */}
          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4 rounded-lg shadow-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-amber-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-amber-800">Aviso importante</h3>
                <div className="mt-1 text-sm text-amber-700">
                  Las órdenes tienen un período de <span className="font-semibold">30 días después de finalizadas</span> para ser validadas. En caso contrario, se cerrarán automáticamente considerándose completas sin posibilidad de reclamación posterior.
                </div>
              </div>
            </div>
          </div>          {/* Orders Table with modern styling */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden w-full">
            {isLoading ? (
              <div className="flex justify-center items-center p-8">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                <span className="ml-3 text-gray-700">Cargando...</span>
              </div>            ) : filteredOrders.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full table-auto">                    <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-normal">DO</th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-normal">Truck</th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-normal">Cliente</th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-normal">Destino</th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-normal">Cita</th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-normal">Arribo</th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-normal">Estatus</th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-normal">SubEstatus</th>
                      <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-normal">
                        <div className="flex items-center justify-center">
                          <Camera size={16} className="mr-1" />
                        </div>
                      </th>
                      <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-normal">
                        <div className="flex items-center justify-center">
                          <FileText size={16} className="mr-1" />
                        </div>
                      </th>
                      <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-normal">
                        <div className="flex items-center justify-center">
                          <CheckCircle size={16} className="mr-1" />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredOrders.map((order) => (
                      <tr 
                        key={order.id} 
                        className="hover:bg-gray-50 transition-colors duration-150 ease-in-out cursor-pointer"
                        onClick={() => handleOrderClick(order.Order_number)}
                      >                          <td className="px-2 py-2 text-sm font-medium text-gray-900">{order.Order_number}</td>
                        <td className="px-2 py-2 text-sm text-gray-500">{order.Truck || 'N/A'}</td>
                        <td className="px-2 py-2 text-sm text-gray-500 max-w-[120px] truncate" title={order.customer}>{order.customer}</td>
                        <td className="px-2 py-2 text-sm text-gray-500 max-w-[100px] truncate" title={order.City}>{order.City}</td>
                        <td className="px-2 py-2 text-sm text-gray-500">
                          {formatAppointmentDateTime(order.Appointment, order.Appointment_hr)}
                        </td>
                        <td className="px-2 py-2 text-sm text-gray-500">{formatDate(getClientATA(order))}</td>
                        <td className="px-2 py-2 text-sm">{getStatusBadge(order.Order_status)}</td>
                        <td className="px-2 py-2 text-sm text-gray-500 max-w-[100px] truncate" title={order.order_Substatus}>{order.order_Substatus || '-'}</td>
                        <td className="px-2 py-2 text-center">
                          {hasPhotoEvidence(order) ? (
                            <CheckCircle size={18} className="text-green-500 mx-auto" />
                          ) : (
                            <XCircle size={18} className="text-red-500 mx-auto" />
                          )}
                        </td>                        
                        <td className="px-2 py-2 text-center">
                          {hasPdfEvidence(order) ? (
                            <CheckCircle size={18} className="text-green-500 mx-auto" />
                          ) : (
                            <XCircle size={18} className="text-red-500 mx-auto" />
                          )}
                        </td>                          <td className="px-2 py-2 text-center">
                          {isOrderValidated(order.Order_number) ? (
                            <div className="flex items-center justify-center">
                              <span 
                                className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium flex items-center"
                                title={`Validada por: ${validatedOrders[order.Order_number]?.user || 'Usuario'} el ${
                                  new Date(validatedOrders[order.Order_number]?.timestamp).toLocaleString('es-ES')
                                }`}
                              >
                                <CheckCircle size={14} className="mr-1" />
                                Validada
                              </span>
                            </div>
                          ) : (                            <button 
                              className="bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1 rounded text-xs font-medium transition-colors"
                              onClick={(e) => handleValidateOrder(order.Order_number, e)}
                              disabled={!VALIDATION_ELIGIBLE_STATUSES.includes(order.Order_status)}
                              title={
                                !VALIDATION_ELIGIBLE_STATUSES.includes(order.Order_status)
                                  ? 'Solo se pueden validar órdenes entregadas o finalizadas' 
                                  : 'Validar esta orden'
                              }
                              style={{
                                opacity: !VALIDATION_ELIGIBLE_STATUSES.includes(order.Order_status) ? 0.5 : 1,
                                cursor: (order.Order_status !== 'ENTREGADO' && order.Order_status !== 'FINALIZADO') ? 'not-allowed' : 'pointer'
                              }}
                            >
                              Validar
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center">
                <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                <h3 className="mt-4 text-lg font-medium text-gray-900">No se encontraron órdenes</h3>
                <p className="mt-2 text-sm text-gray-500">Intenta ajustando los filtros de búsqueda.</p>
              </div>
            )}
          </div>
          
          {/* Results Summary */}
          {filteredOrders.length > 0 && (
            <div className="flex justify-between items-center mt-4 text-sm text-gray-600">
              <div>
                Mostrando {filteredOrders.length} de {orders.length} órdenes
              </div>
            </div>
          )}
        </div>      </div>      {/* Toast Container para react-toastify */}
      <ToastContainer 
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss={true}
        draggable={true}
        pauseOnHover={true}
        theme="colored"
      />
    </div>
  );
};

export default OrderGrid;
